import Config from '../utils/Config';

class NotificationService {
    constructor() {
        this.eventSource = null;
        this.isConnected = false;
        this.currentUserId = null;
        this.listeners = new Map();
        this.keepAliveInterval = null;
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 5;
        this.reconnectTimeout = null; // Store reconnection timeout to be able to clear it
        
        // Disconnect any existing connections if SSE is disabled
        this.checkAndDisconnectIfDisabled();
    }
    
    // Check if SSE is disabled and disconnect if so
    checkAndDisconnectIfDisabled() {
        if (!Config.ENABLE_SSE && this.eventSource) {
            // Silently disconnect - no logging needed
            this.disconnect();
        }
    }

    // Connect to SSE stream for a specific user
    connect(userId) {
        // Check if SSE is enabled via environment variable FIRST - this is the primary gate
        if (!Config.ENABLE_SSE) {
            // Silently disconnect any existing connection
            if (this.eventSource) {
                this.disconnect();
            }
            return;
        }

        if (!userId) {
            console.error('NotificationService: No userId provided for connection');
            return;
        }

        // If already connected to the same user, don't reconnect
        if (this.eventSource && this.currentUserId === userId && this.isConnected) {
            console.log(`NotificationService: Already connected to user ${userId}, skipping reconnection`);
            return;
        }

        if (this.eventSource) {
            console.log('NotificationService: Disconnecting existing connection');
            this.disconnect();
        }

        // Double-check SSE is still enabled before creating EventSource
        if (!Config.ENABLE_SSE) {
            return;
        }
        
        console.log(`NotificationService: Connecting to SSE with userId: ${userId}`);
        const url = `${Config.BACKEND_URL}/api/notifications/stream/${userId}`;
        console.log(`NotificationService: SSE URL: ${url}`);
        
        this.currentUserId = userId;
        this.eventSource = new EventSource(url);

        this.eventSource.onopen = () => {
            // Double-check if SSE is still enabled when connection opens
            if (!Config.ENABLE_SSE) {
                // Silently disconnect - no logging needed
                this.disconnect();
                return;
            }
            
            console.log('SSE connection opened');
            this.isConnected = true;
            this.reconnectAttempts = 0; // Reset reconnect attempts on successful connection
            this.notifyListeners('connection', { status: 'connected' });
            
            // Set up page visibility handling
            this.setupPageVisibilityHandling();
        };

        this.eventSource.onmessage = (event) => {
            // Check if SSE is still enabled before processing ANY messages (including pings)
            if (!Config.ENABLE_SSE) {
                // Silently disconnect - no logging needed
                this.disconnect();
                return;
            }
            
            try {
                const data = JSON.parse(event.data);
                
                // Handle keep-alive pings (only log if SSE is enabled)
                if (data.type === 'ping') {
                    // Don't log ping messages if SSE is disabled (shouldn't reach here, but just in case)
                    if (Config.ENABLE_SSE) {
                        console.log('Received keep-alive ping from server');
                    }
                    this.reconnectAttempts = 0; // Reset reconnect attempts on successful ping
                    return;
                }
                
                console.log('SSE message received:', data);
                
                // Notify specific listeners for this type
                this.notifyListeners(data.type, data);
                
                // Notify general listeners
                this.notifyListeners('message', data);
            } catch (error) {
                console.error('Error parsing SSE message:', error);
            }
        };

        this.eventSource.onerror = (error) => {
            // Check if SSE is disabled FIRST - if so, silently disconnect and don't attempt to reconnect
            if (!Config.ENABLE_SSE) {
                // Clear any pending reconnection timeout
                if (this.reconnectTimeout) {
                    clearTimeout(this.reconnectTimeout);
                    this.reconnectTimeout = null;
                }
                // Silently disconnect - no logging needed
                this.disconnect();
                return;
            }
            
            console.error('SSE connection error:', error);
            this.isConnected = false;
            this.notifyListeners('connection', { status: 'error', error });
            
            // Attempt to reconnect with exponential backoff (only if SSE is enabled)
            // Double-check SSE is still enabled before setting up reconnection
            if (!Config.ENABLE_SSE) {
                // SSE was disabled, silently abort
                return;
            }
            
            if (this.reconnectAttempts < this.maxReconnectAttempts) {
                this.reconnectAttempts++;
                const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000); // Max 30 seconds
                console.log(`Attempting to reconnect SSE in ${delay/1000}s (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);
                
                this.reconnectTimeout = setTimeout(() => {
                    // Check again if SSE is enabled before reconnecting
                    if (!Config.ENABLE_SSE) {
                        // Silently abort - no logging needed
                        this.reconnectTimeout = null;
                        return;
                    }
                    if (!this.isConnected && this.currentUserId) {
                        this.connect(this.currentUserId);
                    }
                    this.reconnectTimeout = null;
                }, delay);
            } else {
                console.error('Max reconnection attempts reached. SSE connection failed.');
                this.notifyListeners('connection', { status: 'failed' });
            }
        };
    }

    // Disconnect from SSE stream
    disconnect() {
        // Clear any pending reconnection timeout first
        if (this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout);
            this.reconnectTimeout = null;
        }
        
        if (this.eventSource) {
            console.log('NotificationService: Disconnecting from SSE');
            this.eventSource.close();
            this.eventSource = null;
            this.isConnected = false;
            this.currentUserId = null;
            this.reconnectAttempts = 0;
            
            // Clear any existing keep-alive interval
            if (this.keepAliveInterval) {
                clearInterval(this.keepAliveInterval);
                this.keepAliveInterval = null;
            }
            
            // Remove page visibility handler
            if (this.visibilityChangeHandler) {
                document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
                this.visibilityChangeHandler = null;
            }
            
            this.notifyListeners('connection', { status: 'disconnected' });
        }
    }

    // Add event listener
    addEventListener(eventType, callback) {
        if (!this.listeners.has(eventType)) {
            this.listeners.set(eventType, []);
        }
        this.listeners.get(eventType).push(callback);
    }

    // Remove event listener
    removeEventListener(eventType, callback) {
        if (this.listeners.has(eventType)) {
            const callbacks = this.listeners.get(eventType);
            const index = callbacks.indexOf(callback);
            if (index > -1) {
                callbacks.splice(index, 1);
            }
        }
    }

    // Notify all listeners for a specific event type
    notifyListeners(eventType, data) {
        if (this.listeners.has(eventType)) {
            this.listeners.get(eventType).forEach(callback => {
                try {
                    callback(data);
                } catch (error) {
                    console.error('Error in notification callback:', error);
                }
            });
        }
    }

    // Get connection status
    getConnectionStatus() {
        return this.isConnected && this.eventSource && this.eventSource.readyState === EventSource.OPEN;
    }

    // Set up page visibility handling to reconnect when page becomes visible
    setupPageVisibilityHandling() {
        // Remove existing listener if any
        if (this.visibilityChangeHandler) {
            document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
        }

        this.visibilityChangeHandler = () => {
            if (document.visibilityState === 'visible') {
                // Check if SSE is enabled before attempting to reconnect
                if (!Config.ENABLE_SSE) {
                    return;
                }
                console.log('Page became visible, checking SSE connection...');
                // Check if connection is still alive
                if (!this.getConnectionStatus() && this.currentUserId) {
                    console.log('SSE connection lost while page was hidden, reconnecting...');
                    this.connect(this.currentUserId);
                }
            }
        };

        document.addEventListener('visibilitychange', this.visibilityChangeHandler);
    }
}

// Create a singleton instance
const notificationService = new NotificationService();

export default notificationService;
